import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { UploadCloud } from "react-feather";
import { C } from "../../reporting/reportingStyles";
import { Loader } from "../../reporting/ReportingShared";
import { errorToast, successToast } from "../../../shared/Toast";
import { listMedia, uploadMedia, MEDIA_LIMITS } from "../../../utils/mediaApi";
import { primaryBtnStyle, secondaryBtnStyle, withDisabled } from "../../common/FormKit";

/**
 * Pick an image from the merchant's Media library (or upload one into it) for a receipt image line.
 * Returns the image's permanent public link — the POS downloads it once when the template arrives.
 */
const ACCEPT = MEDIA_LIMITS.imageMimeTypes.join(",");

const thumb = {
  aspectRatio: "1 / 1",
  background: `repeating-conic-gradient(${C.bg} 0% 25%, ${C.white} 0% 50%) 50% / 16px 16px`,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  overflow: "hidden",
  borderRadius: 10,
  border: `1px solid ${C.border}`,
  cursor: "pointer",
};

const MediaPickerModal = ({ isOpen, toggle, merchantId, onPick }) => {
  const fileInput = useRef(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [items, setItems] = useState([]);

  const load = useCallback(async () => {
    if (!merchantId) return;
    setLoading(true);
    const d = await listMedia({ merchantId, page: 1, limit: 96 });
    setItems(d?.items || []);
    setLoading(false);
  }, [merchantId]);

  useEffect(() => {
    if (isOpen) load();
  }, [isOpen, load]);

  const upload = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    for (const f of files) {
      if (!MEDIA_LIMITS.imageMimeTypes.includes(f.type)) { errorToast(`${f.name}: image must be JPG, PNG or WebP`); return; }
      if (f.size > MEDIA_LIMITS.imageBytes) { errorToast(`${f.name}: image must be 5 MB or smaller`); return; }
    }
    setUploading(true);
    try {
      const res = await uploadMedia(merchantId, files.slice(0, MEDIA_LIMITS.perUpload));
      if (res) {
        successToast("Image uploaded");
        await load();
        const first = res?.items?.[0];
        if (first?.url && files.length === 1) onPick(first.url);
      }
    } finally {
      setUploading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" centered>
      <ModalHeader toggle={toggle}>Choose an image</ModalHeader>
      <ModalBody style={{ maxHeight: "60vh", overflowY: "auto" }}>
        {!merchantId ? (
          <p style={{ color: C.textLight, margin: 0 }}>Select a merchant first.</p>
        ) : loading ? (
          <Loader />
        ) : items.length === 0 ? (
          <p style={{ color: C.textLight, margin: 0 }}>No images in this merchant&apos;s media library yet. Upload one below.</p>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(110px, 1fr))", gap: 12 }}>
            {items.map((item) => (
              <button
                key={item.id}
                type="button"
                title={item.title || item.originalName || `Image ${item.id}`}
                style={{ ...thumb, padding: 0 }}
                onClick={() => onPick(item.url)}
                data-testid={`media-pick-${item.id}`}
              >
                <img src={item.url} alt={item.title || item.originalName || ""} style={{ maxWidth: "100%", maxHeight: "100%" }} />
              </button>
            ))}
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <input ref={fileInput} type="file" accept={ACCEPT} onChange={(e) => { upload(e.target.files); e.target.value = ""; }} style={{ display: "none" }} data-testid="media-picker-file-input" />
        <button type="button" style={withDisabled(secondaryBtnStyle, uploading || !merchantId)} disabled={uploading || !merchantId} onClick={() => fileInput.current?.click()}>
          <UploadCloud size={14} /> {uploading ? "Uploading…" : "Upload new"}
        </button>
        <button type="button" style={primaryBtnStyle} onClick={toggle}>Close</button>
      </ModalFooter>
    </Modal>
  );
};

MediaPickerModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  merchantId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onPick: PropTypes.func.isRequired,
};

export default MediaPickerModal;
